import * as React from 'react';
import { Builder } from '@builder.io/react';
import {
	areSameFilterCategories,
	getCachedFilterCategories,
	getFilterCategoriesForEditor,
} from '@/api/cms/blogFilterCategories';
import image from '@/assets/images/cms/blogOverview.svg?inline';
import { CmsFilterCategoryEntry } from '@/types/filterCategory';

/**
 * A CMS reference to a navigation link, which only carries the path of the page it points at.
 * Small enough to enrich, unlike a reference to the page itself, which drags its whole block tree.
 */
export interface BlogOverviewArticleReference {
	readonly value?: { readonly data?: { readonly path?: string } };
}

/** One picked category. Only the stable key is stored, so renaming a category leaves blocks alone. */
export interface BlogOverviewCategorySelection {
	readonly key?: string;
}

export interface BlogOverviewComponentProps {
	readonly categories?: readonly BlogOverviewCategorySelection[];
	/**
	 * The article lifted above the grid, picked from the navigation links. Nothing picked
	 * falls back to the newest article in the categories above.
	 */
	readonly featuredArticle?: BlogOverviewArticleReference;
}

const registerComponent = <TProps extends BlogOverviewComponentProps>(
	Component: React.FC<TProps>,
	categories: readonly CmsFilterCategoryEntry[],
): void => {
	Builder.registerComponent(Component, {
		name: 'BlogOverview',
		image,
		inputs: [
			{
				name: 'categories',
				type: 'list',
				defaultValue: [],
				required: true,
				helperText:
					categories.length > 0
						? 'Articles assigned to one of these categories are shown. The categories themselves are maintained in the Blog filter categories model.'
						: 'No blog filter categories could be loaded. Check that the Blog filter categories model has published entries.',
				subFields: [
					{
						name: 'key',
						type: 'string',
						required: true,
						// Editors choose by name, the stable key is what gets stored
						enum: categories.map((category) => ({ label: category.name, value: category.key })),
					},
				],
			},
			{
				name: 'featuredArticle',
				type: 'reference',
				model: 'navigation-links',
				helperText:
					'The article shown above the grid. Left empty, the newest article in the categories above is used.',
			},
		],
	});
};

const register = <TProps extends BlogOverviewComponentProps>(Component: React.FC<TProps>): void => {
	// registerComponent is synchronous, so register with whatever is cached and register again
	// once the categories arrive, the same way TeaserCardSlider fills its tag suggestions.
	const initialCategories = getCachedFilterCategories();
	registerComponent(Component, initialCategories);

	// The category list only fills the dropdown in the settings panel, so a visitor never
	// has to pay for fetching it. Both flags are resolved by the SDK at load, before this runs.
	if (typeof window === 'undefined' || !(Builder.isEditing || Builder.isPreviewing)) {
		return;
	}

	void getFilterCategoriesForEditor().then((categories) => {
		if (areSameFilterCategories(initialCategories, categories)) {
			return;
		}

		registerComponent(Component, categories);
	});
};

export default register;
